import { AfterViewInit, Directive, ElementRef, EventEmitter, Input, OnDestroy, Output, Renderer2 } from '@angular/core';
import { fromEvent, Subscription } from 'rxjs';

export interface SortableGridItems {
  useLogicManually?: boolean;
}

@Directive({
  standalone: false,
  selector: '[tiSortableGridItemsDnd]',
})
export class SortableGridItemsDndDirective implements AfterViewInit, OnDestroy {
  @Input('tiSortableGridItemsDnd') set config(options: SortableGridItems) {
    this.options = options || {};
    this.initialized && this.updateSubscriptionOnSortableRows();
  }

  @Output() orderItemChange = new EventEmitter<{
    draggedIndex: number;
    dropItem: number;
    wasChanged: (value: boolean) => void;
  }>();

  private initialized: boolean;
  private options: SortableGridItems = {};
  private sub: Subscription[] = [];
  private draggedItemIndex: number;

  constructor(
    private gridElement: ElementRef<HTMLElement>,
    private renderer: Renderer2,
  ) {}

  ngAfterViewInit() {
    this.initialized = true;
    this.updateSubscriptionOnSortableRows();
  }

  updateSubscriptionOnSortableRows() {
    this.markRowsAsDraggable();

    if (!this.gridElement.nativeElement || this.options.useLogicManually) {
      return;
    }

    this.unsubscribe();
    const dragStart = fromEvent<DragEvent>(document, 'dragstart');
    const dragOver = fromEvent(document, 'dragover');

    this.sub.push(dragStart.subscribe((e: DragEvent) => this.handleDragStart(e)));
    this.sub.push(dragOver.subscribe((e: DragEvent) => this.handleDragOver(e)));
  }

  handleDragStart({ target }): number {
    const row: HTMLTableRowElement = <HTMLTableRowElement>target;
    this.draggedItemIndex = 'rowIndex' in row ? row.rowIndex : null; // should check to can be sure that is row

    return this.draggedItemIndex;
  }

  handleDragOver(e: DragEvent) {
    const node = this.gridElement.nativeElement.contains(e.target as Node)
      ? this.closest(e.target, this.tableRow)
      : null;
    const dropIndex = node ? node.rowIndex : null;
    if (this.draggedItemIndex !== null && dropIndex !== null && dropIndex !== this.draggedItemIndex) {
      new Promise((changeResolver) => {
        this.orderItemChange.emit({
          draggedIndex: this.draggedItemIndex,
          dropItem: dropIndex,
          wasChanged: changeResolver,
        });
        this.draggedItemIndex = dropIndex;
      }).then((wasChanged) => wasChanged && this.changeDataAttributeToRow(this.draggedItemIndex, dropIndex));
    }
  }

  markRowAsDraggableByIndex(index: number) {
    const rowItems = this.getRowsByIndex(index);
    if (rowItems) {
      Array.from(rowItems).forEach((item) => this.renderer.setAttribute(item, 'draggable', 'true')); // rows should be marked like draggable to get right e.target
    }
  }

  private markRowsAsDraggable() {
    if (!this.gridElement.nativeElement) {
      return;
    }

    const tableRows = Array.from(this.gridElement.nativeElement.querySelectorAll('.k-grid tr'));
    tableRows.forEach((row) => {
      this.renderer.setAttribute(row, 'draggable', 'true'); // rows should be marked like draggable to get right e.target
    });
  }

  private tableRow = (node: HTMLElement): boolean => node.tagName.toLowerCase() === 'tr';

  private closest = (node, predicate: (node) => boolean): HTMLTableRowElement => {
    try {
      while (node && !predicate(node)) {
        node = node.parentNode;
      }

      return node;
    } catch (e) {
      return null;
    }
  };

  private unsubscribe() {
    if (this.sub) {
      this.sub.forEach((s) => s.unsubscribe());
      this.sub = [];
    }
  }

  private changeDataAttributeToRow(draggedIndex: number, dropIndex: number) {
    const draggedNodes = this.getRowsByIndex(draggedIndex);
    const droppedNodes = this.getRowsByIndex(dropIndex);

    const changeNodeAttr = (nodes: NodeListOf<Element>, index: string) => {
      Array.from(nodes).forEach((n) => n.setAttribute('data-kendo-grid-item-index', index));
    };

    changeNodeAttr(draggedNodes, dropIndex.toString());
    changeNodeAttr(droppedNodes, draggedIndex.toString());
  }

  private getRowsByIndex(index: number): NodeListOf<Element> {
    const grid = this.gridElement.nativeElement;
    if (grid.querySelector(`.k-detail-row`)) {
      return [] as any;
    }

    return grid.querySelectorAll(`tr[data-kendo-grid-item-index="${index}"]`);
  }

  ngOnDestroy() {
    this.unsubscribe();
  }
}
